"use client";

import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useState,
} from "react";
import { useHasConsent } from "./hooks";

/**
 * Shares the ONE `/api/privacy-region` resolution across the whole app so
 * every consumer (PostHogProvider's analytics gate, the preference-cookie
 * gate below) reads the same signal instead of each firing its own fetch.
 * `null` = not yet resolved. Fails closed: any error / unresolved state
 * leaves this `false`-equivalent for callers (see {@link useCanWritePreferenceCookies}).
 */
const LegitimateInterestContext = createContext<boolean | null>(null);

export function ConsentRegionProvider({ children }: { children: ReactNode }) {
	const analyticsAllowed = useHasConsent("analytics");
	const [legitimateInterestOk, setLegitimateInterestOk] = useState<
		boolean | null
	>(null);

	// Resolve once. Skip the fetch entirely once the user has already granted
	// "analytics" consent — consent covers every region, so the
	// legitimate-interest path is irrelevant for that consumer. Other
	// consumers (e.g. the preferences gate) still check their own consent
	// category independently.
	useEffect(() => {
		if (analyticsAllowed) return;
		let cancelled = false;
		fetch("/api/privacy-region")
			.then((r) => (r.ok ? r.json() : null))
			.then((d: unknown) => {
				// Only flip to legitimate-interest when the endpoint explicitly says
				// consent is NOT required; any other shape (null, missing field)
				// leaves it false — fail closed.
				const consentRequired =
					d && typeof d === "object"
						? (d as { consentRequired?: unknown }).consentRequired
						: undefined;
				if (!cancelled) setLegitimateInterestOk(consentRequired === false);
			})
			.catch(() => {
				if (!cancelled) setLegitimateInterestOk(false);
			});
		return () => {
			cancelled = true;
		};
	}, [analyticsAllowed]);

	return (
		<LegitimateInterestContext.Provider value={legitimateInterestOk}>
			{children}
		</LegitimateInterestContext.Provider>
	);
}

/** `null` = region not yet resolved; `true` = outside the prior-consent zone. */
export function useLegitimateInterest(): boolean | null {
	return useContext(LegitimateInterestContext);
}

/**
 * True once first-party preference-personalization cookies (`ld_pref_v1`,
 * `ld_filters_v1`, `hunt_interest_v1`, `ld_recent_jobs_v1`, `ld_seen_v1`) may
 * be written for this visitor: either they explicitly granted the
 * "preferences" consent category, or they're outside the prior-consent
 * regions (EU/EEA/UK/CH) on a legitimate-interest basis — mirrors the
 * analytics gate in `posthog-provider.tsx`. Fails closed: an unresolved
 * region (`null`) is treated as "consent required", so a prior-consent-region
 * visitor never gets a personalization cookie before we can prove otherwise.
 */
export function useCanWritePreferenceCookies(): boolean {
	const hasPreferences = useHasConsent("preferences");
	const legitimateInterestOk = useLegitimateInterest();
	return hasPreferences || legitimateInterestOk === true;
}
